import { SITE } from "@/constants";
import { Helmet } from "react-helmet-async";

/**
 * PageMeta component for setting page-specific metadata
 * @param {Object} props - Component props
 * @param {string} props.title - Page title (will be appended to site name)
 * @param {string} props.description - Page description
 * @param {string} props.image - URL to page-specific image
 * @param {string} props.canonicalUrl - Custom canonical URL (optional)
 * @param {string} props.type - Content type (default: website)
 * @param {string} props.twitterCardType - Twitter card type (default: summary_large_image)
 * @returns {JSX.Element} Helmet component with page meta tags
 */
export default function PageMeta({
  title,
  description,
  image,
  canonicalUrl,
  type = "website",
  twitterCardType = "summary_large_image",
}) {
  // Use defaults if values are not provided
  const pageTitle = title ? `${title} | ${SITE.name}` : SITE.title;
  const pageDescription = description || SITE.description;

  // Define full URL for images
  const baseUrl = SITE.url || "https://lexi.tips";
  // Ensure baseUrl doesn't end with a slash and path starts with a slash
  const baseUrlNormalized = baseUrl.endsWith("/")
    ? baseUrl.slice(0, -1)
    : baseUrl;
  const ogImageUrl = image
    ? `${baseUrlNormalized}${image.startsWith("/") ? image : `/${image}`}`
    : `${baseUrlNormalized}/images/lexi-social-share.jpg`;
  const canonical = canonicalUrl
    ? `${baseUrlNormalized}${canonicalUrl.startsWith("/") ? canonicalUrl : `/${canonicalUrl}`}`
    : SITE.url;

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1920" />
      <meta property="og:image:height" content="1080" />
      <meta property="og:image:alt" content={pageTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCardType} />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={pageTitle} />
    </Helmet>
  );
}
